// M3 offshore operations: read-only views for the UI and renderers (pure — never mutate state).
import { eqDef, fieldDef, stat, yf } from '../defs.js';
import { m3On, plotWater, rigBuiltYear, envelope, envelopeProblem, seasonFor, month, fieldMaxWater } from './opsutil.js';
import { platformsView, platformQuote } from './platforms.js';
import { logisticsView } from './logistics.js';
import { charterQuote, charterBoardView } from './charters.js';
import { eorView } from './eor.js';
import { megaprojectsView } from './megaprojects.js';
import { bopDef } from './offshore.js';

export { platformsView, platformQuote, logisticsView, charterQuote, charterBoardView, eorView, megaprojectsView };

const STRUCT = { tender: 'tender', swamp_barge: 'posted_barge', barge: 'sub_barge', platform: 'jacket', jackup: 'jackup', semisub: 'semisub', drillship: 'drillship' };
const PSTRUCT = { lake: 'jacket', jacket_shallow: 'jacket', jacket: 'jacket', jacket_deep: 'jacket', gbs: 'gravity', tower: 'tower', tlp: 'tlp', spar: 'spar', fps: 'fps', fpso: 'fpso', subsea: 'subsea' };

// Water depth per plot (for fieldView); [] for land fields.
export function plotWaters(fieldId) {
  const d = fieldDef(fieldId);
  if (!d || !fieldMaxWater(d)) return null;
  const n = (d.grid?.w ?? 6) * (d.grid?.h ?? 4);
  return Array.from({ length: n }, (_, i) => plotWater(fieldId, i));
}

// Extra fieldView block (spread into the view).
export function fieldM3View(state, fieldId) {
  const d = fieldDef(fieldId);
  const f = state.fields[fieldId];
  if (!d || !f) return {};
  const water = plotWaters(fieldId);
  return {
    offshore: !!water, waterDepths: water, environment: d.environment ?? null, distanceToShoreMi: d.distanceToShoreMi ?? null, shoreBase: d.shoreBase ?? null,
    sea: f.sea ?? null, storm: f.storm ?? null, season: seasonFor(d, month(state)), serviceOnly: d.serviceOnly ?? null, outletNote: d.outletNote ?? null,
    platforms: state.platforms ? platformsView(state, fieldId) : [],
  };
}

export function rigEnvelope(rig) {
  const def = eqDef(rig.defId);
  const e = envelope(def, rigBuiltYear(rig));
  return { minWater: e.minWater, maxWater: e.maxWater, maxDepth: e.maxDepth, stationKeeping: e.stationKeeping, rigClass: e.rigClass, bopType: e.bopType,
    opLimitHsFt: e.opLimitHsFt, survivalHsFt: e.survivalHsFt, floating: e.floating, needsPlatform: e.needsPlatform, pob: e.pob };
}

// Extra rigsView row block.
export function rigM3View(state, rig) {
  if (!m3On(state) && !rig.builtYear) return {};
  const def = eqDef(rig.defId);
  const off = !!def?.stats?.offshore;
  return {
    builtYear: rigBuiltYear(rig), offshore: off, envelope: rigEnvelope(rig), structure: off ? (STRUCT[def?.stats?.rigClass] ?? 'jackup') : null,
    location: rig.location ?? (rig.at ? { fieldId: rig.at.fieldId, plot: rig.at.plot } : null), stacked: !!rig.stacked, charterOut: rig.charterOut ?? null,
    supplyDaysLeft: off ? (state.m3?.supply?.[rig.uid] ?? def.stats.deckStorageDays ?? null) : null,
    logistics: off ? Object.values(state.logistics ?? {}).filter((l) => l.assignedTo?.id === rig.uid).map((l) => ({ uid: l.uid, defId: l.defId, name: eqDef(l.defId)?.name })) : [],
    subseaBop: stat(bopDef(state, rig), 'bopType', 'surface') === 'subsea',
  };
}

// For the plot grid while assigning: fits / too deep / too shallow per plot for one rig.
export function rigFitView(state, rigUid, fieldId) {
  const rig = state.rigs[rigUid];
  const d = fieldDef(fieldId);
  if (!rig || !d) return null;
  const def = eqDef(rig.defId);
  const by = rigBuiltYear(rig);
  const n = (d.grid?.w ?? 6) * (d.grid?.h ?? 4);
  return Array.from({ length: n }, (_, i) => {
    const w = plotWater(fieldId, i);
    const why = envelopeProblem(def, by, w, 0);
    const e = envelope(def, by);
    return { idx: i, waterDepthFt: w, fits: !why, badge: !why ? 'fits' : !e.offshore || w <= 0 ? (e.offshore ? 'dry land' : 'water') : w < e.minWater ? 'too shallow' : 'too deep', reason: why };
  });
}

// Extra wellView block (`m3`).
export function wellM3View(state, well) {
  if (!well || (well.offshore === undefined && !m3On(state))) return null;
  const water = well.waterDepthFt ?? plotWater(well.fieldId, well.plot);
  const rig = well.rigId ? state.rigs[well.rigId] : null;
  const rigDef = rig ? eqDef(rig.defId) : well.contract?.rigDefId ? eqDef(well.contract.rigDefId) : null;
  const p = well.platformUid ? state.platforms?.[well.platformUid] : null;
  const pdef = p ? eqDef(p.defId) : null;
  const f = state.fields[well.fieldId];
  const floating = !!rigDef?.stats?.floating;
  const structure = rigDef?.stats?.offshore ? (STRUCT[rigDef.stats.rigClass] ?? 'jackup') : p ? PSTRUCT[pdef?.stats?.platformType] ?? 'jacket' : well.subsea ? 'subsea' : null;
  return {
    waterDepthFt: water, md: Math.round(water + (well.depth ?? 0)), offshore: water > 0, seaState: well.seaState ?? f?.sea?.hsFt ?? 0, storm: f?.storm ?? null,
    evacuated: !!well.evacuated, rigDefId: rigDef?.id ?? null, chartered: !!well.contract?.charter, charter: well.contract?.charter ?? null,
    platform: p ? { uid: p.uid, defId: p.defId, name: pdef?.name, structure: PSTRUCT[pdef?.stats?.platformType] ?? 'jacket', status: p.status } : null,
    subsea: well.subsea ?? null, riser: floating && !['PRODUCING', 'SHUT_IN', 'DEPLETED', 'PLUGGED', 'DRY'].includes(well.status), subseaBop: floating,
    structure, horizontal: well.horizontal ?? null, injector: !!well.injector, firstProdYear: well.firstProdYear ?? null, wowDays: well.wowDays ?? 0, year: Math.floor(yf(state)),
  };
}
